import { ArrowLeft } from 'lucide-react'
import { DashboardCategoryTabs, type DashboardCategory } from './DashboardCategoryTabs'
import { navigate } from '../lib/router'

export type SettingsView = 'account' | 'profile' | 'users'

interface SettingsSubMenuProps {
  activeKey: SettingsView
  /** User Management is only reachable for account administrators. */
  showUserManagement: boolean
  /** The page panel the selected tab controls. */
  panelId?: string
}

const TARGETS: Record<SettingsView, string> = {
  account: '/account',
  profile: '/profile',
  users: '/users',
}

/**
 * The second-level menu for the personal settings area.
 *
 * Same bar as the dashboard categories and the Insights child menu: Back first, every
 * label visible, the active label in the orange pill. User Management stays visible but
 * disabled when the signed-in person may not administer accounts.
 */
export function SettingsSubMenu({
  activeKey,
  showUserManagement,
  panelId,
}: SettingsSubMenuProps) {
  const categories: DashboardCategory[] = [
    { key: 'back', label: 'Back', icon: ArrowLeft },
    { key: 'account', label: 'Account Settings' },
    { key: 'profile', label: 'Profile Settings' },
    {
      key: 'users',
      label: 'User Management',
      disabledReason: 'Only account administrators can open User Management',
    },
  ]
  const disabledKeys = showUserManagement ? [] : ['users']

  const select = (key: string) => {
    if (key === 'back') {
      navigate('/general-overview')
      return
    }
    if (key === 'users' && !showUserManagement) return
    const target = TARGETS[key as SettingsView]
    if (target) navigate(target)
  }

  return (
    <DashboardCategoryTabs
      categories={categories}
      activeKey={activeKey}
      onSelect={select}
      disabledKeys={disabledKeys}
      panelId={panelId}
      ariaLabel="Settings pages"
    />
  )
}

export default SettingsSubMenu
